export type BusinessService = {
  id: string;
  name: string;
  eyebrow: string;
  price: number;
  cadence?: string;
  description: string;
  features: string[];
  action: 'purchase' | 'consultation';
  featured?: boolean;
};

export type BusinessTestimonial = {
  quote: string;
  author: string;
  photo?: string;
};

export type BusinessProfile = {
  id: string;
  username: string;
  name: string;
  shortName: string;
  location: string;
  category: string;
  headline: string;
  intro: string;
  about: string[];
  heroImage: string;
  aboutImage: string;
  instagramUrl: string;
  instagramFollowers: string;
  consultationUrl: string;
  services: BusinessService[];
  testimonials: BusinessTestimonial[];
};

const coachingDescription = 'A fully personalized plan for burning fat, building muscle, and making lasting progress—with weekly reviews and direct support from Nolan.';

export const nolanKingProfile: BusinessProfile = {
  id: 'nolan-king-training',
  username: 'nolanking',
  name: 'Nolan King Training',
  shortName: 'Nolan King',
  location: 'Miami, Florida',
  category: 'Elite online coaching',
  headline: 'Train with purpose. Build strength that lasts.',
  intro: 'Personal coaching built around your body, your goals, and your real life—not a recycled template.',
  about: [
    'I’ve spent over ten years helping people get stronger, move better, and enjoy training again.',
    'My background spans football, sprinting, basketball, and Brazilian Jiu-Jitsu. What matters most is helping everyday people discover their own version of athleticism. You don’t need to be an athlete to train like one.',
    'Fitness shouldn’t take over your life. It should make every part of it better.',
  ],
  heroImage: 'https://static.wixstatic.com/media/a4c5e6_368bb328d6a0448c9c227dea93356692f000.jpg/v1/fill/w_1280,h_1600,al_c,q_88,enc_avif,quality_auto/a4c5e6_368bb328d6a0448c9c227dea93356692f000.jpg',
  aboutImage: 'https://static.wixstatic.com/media/a4c5e6_27b7206909724747b27d79318250eef9~mv2.jpg/v1/fill/w_900,h_1050,al_c,q_88,enc_avif,quality_auto/unnamed.jpg',
  instagramUrl: 'https://www.instagram.com/king.nolan/',
  instagramFollowers: '12K+',
  consultationUrl: '#services',
  services: [
    {
      id: 'coaching-12-week',
      name: '12 Week Online Coaching',
      eyebrow: 'Best transformation',
      price: 720,
      description: coachingDescription,
      features: ['Custom nutrition plan', 'Complete workout program', 'Supplement guidance', 'Weekly progress reviews', 'Unlimited text & email support'],
      action: 'purchase',
      featured: true,
    },
    {
      id: 'coaching-4-week',
      name: '4 Week Online Coaching',
      eyebrow: 'Focused start',
      price: 320,
      description: coachingDescription,
      features: ['Custom nutrition plan', 'Complete workout program', 'Supplement guidance', 'Weekly progress reviews'],
      action: 'purchase',
    },
    {
      id: 'coaching-8-week',
      name: '8 Week Online Coaching',
      eyebrow: 'Build momentum',
      price: 520,
      description: coachingDescription,
      features: ['Custom nutrition plan', 'Complete workout program', 'Supplement guidance', 'Weekly progress reviews'],
      action: 'purchase',
    },
    {
      id: 'coaching-1-year',
      name: '1 Year Online Coaching',
      eyebrow: 'Long-term mastery',
      price: 2520,
      description: coachingDescription,
      features: ['Year-long programming', 'Nutrition and supplement plans', 'Weekly progress reviews', 'Unlimited direct support'],
      action: 'purchase',
    },
    {
      id: 'nutrition-monthly',
      name: 'Nutrition Coaching',
      eyebrow: 'Nutrition only',
      price: 220,
      cadence: '/ month',
      description: 'A customized nutrition plan for burning fat and building muscle, adjusted through weekly progress reviews.',
      features: ['Custom diet plan', 'Weekly photo reviews', 'Ongoing plan adjustments', 'Unlimited email support'],
      action: 'purchase',
    },
    {
      id: 'custom-training-plan',
      name: 'Custom Training Plan',
      eyebrow: 'Train independently',
      price: 220,
      description: 'A complete training plan designed around your specific needs, experience, schedule, and goals.',
      features: ['Personalized programming', 'Built for your schedule', 'Goal-specific progression'],
      action: 'purchase',
    },
    {
      id: 'consultation-30',
      name: '30-Minute Consultation',
      eyebrow: 'Get expert direction',
      price: 135,
      description: 'A focused phone or video session to answer your questions about nutrition, training, or your next step.',
      features: ['Phone or video call', 'Training and nutrition Q&A', 'Clear next steps'],
      action: 'consultation',
    },
  ],
  testimonials: [
    {
      quote: 'My body has been transforming every week. I have never been so strong and lean in my entire life. Nolan responds right away and makes sure I stay on top of the program.',
      author: 'Paul R.',
      photo: '/business/nolanking/testimonials/paul-r.avif',
    },
    {
      quote: 'Nolan is amazing. He is always available to answer my questions, always a step ahead, and I know he genuinely cares about my success.',
      author: 'Caroline S.',
      photo: '/business/nolanking/testimonials/caroline-s.avif',
    },
    {
      quote: 'Both my mind and my body are in better shape, thanks to Nolan. Training with this champion may be the best decision I’ve ever made.',
      author: 'Robert M.',
    },
    {
      quote: 'Eight months after shoulder surgery, I’m in better shape than I was before it. I have more energy and I’m not feeling sorry for myself anymore.',
      author: 'Jordan W.',
      photo: '/business/nolanking/testimonials/jordan-w.avif',
    },
    {
      quote: 'His personal training and nutrition program transformed my energy levels. He catches every detail—from programming and form to grip and stance.',
      author: 'Narsing B.',
      photo: '/business/nolanking/testimonials/narsing-b.avif',
    },
    {
      quote: 'The motivation and personal attention make it worth every penny. Nolan keeps me comfortable while always pushing me to the next level, and the results speak for themselves.',
      author: 'Hammad R.',
      photo: '/business/nolanking/testimonials/hammad-r.avif',
    },
    {
      quote: 'Smart, reasoned, and experienced advice. Nolan is attuned to your needs and consistently gives nuanced, constructive feedback.',
      author: 'Tony Z.',
      photo: '/business/nolanking/testimonials/tony-z.avif',
    },
    {
      quote: 'Nolan works around my travel schedule, keeps every session fresh, and knows how to push me while properly handling my injuries. I confidently recommend him.',
      author: 'Nicole B.',
    },
  ],
};

export const businessProfiles: Record<string, BusinessProfile> = {
  [nolanKingProfile.username]: nolanKingProfile,
};
